import { createContext, useContext } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { firma } from '../content';
import './SiteShell.css';

/** Die drei Routen der Seite. */
export type Path = '/' | '/impressum' | '/datenschutz';

export interface NavState {
  path: string;
  navigate: (to: Path) => void;
}

/**
 * Mini-Router-Kontext ohne externe Bibliothek (Begruendung siehe App.tsx).
 * Liegt hier statt in App.tsx, damit die Importrichtung App -> SiteShell
 * bleibt und kein Zirkelbezug zwischen den beiden Dateien entsteht - Home,
 * Impressum und Datenschutz brauchen den Link-Baustein aus dieser Datei
 * ebenfalls.
 */
export const NavContext = createContext<NavState | null>(null);

export function useNav(): NavState {
  const ctx = useContext(NavContext);
  if (!ctx) {
    throw new Error('useNav() nur innerhalb von <NavContext.Provider> verwendbar.');
  }
  return ctx;
}

/**
 * Interner Link ueber die History-API statt vollem Seitenwechsel. Bleibt
 * trotzdem ein echtes <a href>: Rechtsklick, Mittelklick und "In neuem Tab
 * oeffnen" funktionieren wie gewohnt.
 */
export function AppLink({
  to,
  children,
  className,
  current,
}: {
  to: Path;
  children: ReactNode;
  className?: string;
  current?: boolean;
}) {
  const { navigate } = useNav();

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  }

  return (
    <a href={to} className={className} aria-current={current ? 'page' : undefined} onClick={handleClick}>
      {children}
    </a>
  );
}

const navItems: Array<{ label: string; to: Path }> = [
  { label: 'Startseite', to: '/' },
  { label: 'Impressum', to: '/impressum' },
  { label: 'Datenschutz', to: '/datenschutz' },
];

/**
 * Kopf mit typografischer Wortmarke (kein Logo-Bild vorhanden) und
 * Navigation, Fuss mit den Pflichtverweisen. Die Navigation ist eine
 * schlichte, umbrechende Zeile - bewusst kein Hamburger-Menu mit
 * Ueberlagerung, das passt nicht zur Ruhe der Marke.
 */
export function SiteShell({ children }: { children: ReactNode }) {
  const { path } = useNav();
  const year = new Date().getFullYear();

  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="container site-header__inner">
          <AppLink to="/" className="site-header__wordmark">
            {firma.name}
          </AppLink>
          <nav className="site-nav" aria-label="Hauptnavigation">
            {navItems.map((item) => (
              <AppLink
                key={item.to}
                to={item.to}
                current={path === item.to}
                className={`site-nav__link${path === item.to ? ' is-active' : ''}`}
              >
                {item.label}
              </AppLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="site-main">{children}</main>

      <footer className="site-footer">
        <div className="container site-footer__inner">
          <div className="site-footer__brand">
            <p className="site-footer__name">{firma.name}</p>
            <p className="site-footer__claim">{firma.claim}</p>
          </div>
          <nav className="site-footer__nav" aria-label="Rechtliches">
            <AppLink to="/impressum" className="site-footer__link">
              Impressum
            </AppLink>
            <AppLink to="/datenschutz" className="site-footer__link">
              Datenschutz
            </AppLink>
            <a className="site-footer__link" href={`mailto:${firma.mailAllgemein}`}>
              {firma.mailAllgemein}
            </a>
          </nav>
          <p className="site-footer__copyright">
            © {year} {firma.name}
          </p>
        </div>
      </footer>
    </div>
  );
}
